import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Card, Col, Form, Row, Table } from 'react-bootstrap';
import Select from 'react-select';
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import debounce from 'lodash/debounce';

function OpeningStockUpdate() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
    const [row, setRow] = useState(null);
    const [openingDate, setOpeningDate] = useState('');
    const [loading, setLoading] = useState(true);

    // 🔹 Fetch All Warehouses
    const getWarehouseData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}warehouse/findAllWarehouse`, {
                headers: authHeader()
            });
            const data = response.data || [];
            const options = data.map(w => ({
                value: w.id,
                label: w.name || w.warehousename
            }));
            setWarehouseOptions(options);
        } catch (error) {
            console.error('Error fetching warehouses:', error);
        }
    };

    // 🔹 Product Search Handling
    const handleProductSearchChange = debounce((inputValue) => {
        setProductSearch(encodeURIComponent(inputValue || " "));
    }, 300);

    const getProductData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
                { headers: authHeader() }
            );

            const products = response.data?.Index || response.data?.data || [];
            const mapped = products.map(product => ({
                value: product.id,
                label: `${product.description} - ${product.serialnumber} - ${product.partno}`,
                description: product.description,
                serialnumber: product.serialnumber,
                partno: product.partno
            }));
            setProductOptions(mapped);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    // 🔹 Fetch Opening Stock Data
    const getOpeningStockData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}openingstock/findById/${id}`, {
                headers: authHeader()
            });

            const stockData = response.data;

            if (stockData) {
                // Set warehouse
                if (stockData.warehouse) {
                    setSelectedWarehouse({
                        value: stockData.warehouse.id,
                        label: stockData.warehouse.name || stockData.warehouse.warehousename
                    });
                }

                // Set product
                if (stockData.product) {
                    const productOption = {
                        value: stockData.product.id,
                        label: `${stockData.product.description} - ${stockData.product.serialnumber} - ${stockData.product.partno}`,
                        description: stockData.product.description,
                        serialnumber: stockData.product.serialnumber,
                        partno: stockData.product.partno
                    };
                    setSelectedProduct(productOption);

                    setRow({
                        description: stockData.product.description,
                        value: stockData.product.id,
                        label: productOption.label,
                        quantity: stockData.quantity.toString(),
                        serialnumber: stockData.product.serialnumber,
                        partno: stockData.product.partno
                    });
                }

                // Set opening date
                if (stockData.openingdate) {
                    const date = new Date(stockData.openingdate);
                    setOpeningDate(date.toISOString().split('T')[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching opening stock:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch opening stock data.'
            });
            navigate("/opening-stock-list/1");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getWarehouseData();
        getOpeningStockData();
    }, [id]);

    useEffect(() => {
        getProductData();
    }, [productSearch]);

    // 🔹 Product Select (only one)
    const handleProductChange = (selectedOption) => {
        setSelectedProduct(selectedOption);

        if (!selectedOption) {
            setRow(null);
            return;
        }

        const newRow = {
            description: selectedOption.description,
            value: selectedOption.value,
            label: selectedOption.label,
            quantity: row?.quantity || '',
            serialnumber: selectedOption.serialnumber,
            partno: selectedOption.partno
        };

        setRow(newRow);
    };

    const handleQuantityChange = (value) => {
        setRow(prev => ({ ...prev, quantity: value }));
    };

    const removeRow = () => {
        setRow(null);
        setSelectedProduct(null);
    };

    // 🔹 Update Handler
    const handleUpdate = async (e) => {
        e.preventDefault();

        if (!selectedWarehouse) {
            Swal.fire({
                icon: 'warning',
                title: 'Warehouse Required',
                text: 'Please select a warehouse.',
            });
            return;
        }

        if (!openingDate) {
            Swal.fire({
                icon: 'warning',
                title: 'Opening Date Required',
                text: 'Please select opening date.',
            });
            return;
        }

        if (!row) {
            Swal.fire({
                icon: 'warning',
                title: 'Product Required',
                text: 'Please select a product.',
            });
            return;
        }

        if (!row.quantity || parseInt(row.quantity) <= 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Quantity Required',
                text: 'Please enter a valid quantity.',
            });
            return;
        }

        const payload = {
            id: parseInt(id),
            product: { id: row.value },
            warehouse: { id: selectedWarehouse.value },
            quantity: parseInt(row.quantity),
            openingdate: openingDate
        };

        try {
            await axios.put(`${BASE_URL}openingstock/update/${id}`, payload, {
                headers: authHeader()
            });
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Opening stock updated successfully!'
            });
            navigate("/opening-stock-list/1");
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data || 'Failed to update opening stock.'
            });
        }
    };

    if (loading) {
        return (

            <Card className="shadow-sm">
                <Card.Body className="text-center py-4">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading opening stock data...</p>
                </Card.Body>
            </Card>

        );
    }

    return (

        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">Update Opening Stock</h5>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={handleUpdate}>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Warehouse *</Form.Label>
                                <Select
                                    value={selectedWarehouse}
                                    onChange={setSelectedWarehouse}
                                    options={warehouseOptions}
                                    placeholder="Select warehouse"
                                    isClearable
                                    required
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Opening Date *</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={openingDate}
                                    onChange={(e) => setOpeningDate(e.target.value)}
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group controlId="productSelect" className="mb-3">
                        <Form.Label>Select Product *</Form.Label>
                        <Select
                            value={selectedProduct}
                            onInputChange={handleProductSearchChange}
                            onChange={handleProductChange}
                            options={productOptions}
                            placeholder="Search or select product"
                            isClearable
                            menuPortalTarget={document.body}
                            styles={{
                                menuPortal: base => ({ ...base, zIndex: 9999 })
                            }}
                        />
                    </Form.Group>

                    {row && (
                        <div className="table-responsive mt-3">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Product Description</th>
                                        <th>Serial Number</th>
                                        <th>Part No</th>
                                        <th>Quantity *</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>1</td>
                                        <td>{row.description}</td>
                                        <td>{row.serialnumber}</td>
                                        <td>{row.partno}</td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                value={row.quantity}
                                                onChange={(e) => handleQuantityChange(e.target.value)}
                                                required
                                                min="1"
                                            />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={removeRow}
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    )}

                    <div className="text-end">
                        <button
                            type="button"
                            className="btn btn-secondary mt-3 me-2"
                            onClick={() => navigate("/opening-stock-list/1")}
                        >
                            Cancel
                        </button>
                        <button className="btn btn-primary mt-3" type="submit">
                            UPDATE
                        </button>
                    </div>
                </Form>
            </Card.Body>
        </Card>

    );
}

export default OpeningStockUpdate;